import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  LightbulbIcon,
} from "lucide-react";

import { formatDateRelative } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { CardContent } from "simplydo/react/ui/card";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

import CircularProgress from "components/lib/CircularProgress";

import {
  HomeLink,
  HomeRow,
  HomeRowActions,
  HomeRowContainer,
  HomeRowContent,
  HomeRowIcon,
} from "../HomeUI";
import { deadlineSortValue, getDateStatus } from "../dateStatus";
import HomeBlock from "./HomeBlock";

type UserIdeasBlockProps = { variant: "draft" | "submitted" };

const UserIdeasBlock = ({ variant }: UserIdeasBlockProps) => {
  const [showMore, setShowMore] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  const user = useCurrentUser();
  const ideaQuery = useQuery(
    initUseQuery(api.users.ideas, [user!._id, null], { staleTime: 30 * 1000 }),
  );
  const isDraft = variant === "draft";

  const ideasData = ideaQuery.data;
  const ideas = useMemo(() => {
    const allIdeas =
      ideasData && !Array.isArray(ideasData) ? ideasData.ideas : [];
    const filtered = allIdeas.filter((idea) =>
      isDraft ? !idea.isSubmitted : idea.isSubmitted,
    );
    if (isDraft) {
      return [...filtered].sort(
        (a, b) =>
          deadlineSortValue(a.ownerChallenge?.deadline) -
          deadlineSortValue(b.ownerChallenge?.deadline),
      );
    }
    return filtered;
  }, [ideasData, isDraft]);

  if (!ideas.length) return null;

  const countPerColumn = 3;
  const visibleIdeas = ideas.slice(
    0,
    showMore ? ideas.length : countPerColumn * 2,
  );
  const columns = visibleIdeas.length > countPerColumn ? 2 : 1;

  return (
    <HomeBlock $width={columns}>
      <CardContent>
        <H3>
          {t(
            isDraft ? "home.ideas.drafts.title" : "home.ideas.submitted.title",
          )}
          <Subheader>
            {t(
              isDraft ? "home.ideas.drafts.info" : "home.ideas.submitted.info",
              { count: ideas.length },
            )}
          </Subheader>
        </H3>
        <HomeRowContainer $columns={columns}>
          {visibleIdeas.map((idea) => {
            const progress = idea.completeness ?? 0;
            const deadlineStatus = getDateStatus(idea.ownerChallenge?.deadline);
            const isElapsed = isDraft && Boolean(deadlineStatus?.elapsed);
            return (
              <HomeRow key={idea._id} data-muted={isElapsed || undefined}>
                {isDraft ? (
                  <CircularProgress
                    size={44}
                    progress={progress}
                    showPercentage
                    color={theme.colors.primary.solid}
                    backgroundColor={theme.colors.primary.soft}
                  />
                ) : (
                  <HomeRowIcon>
                    <Icon icon={LightbulbIcon} />
                  </HomeRowIcon>
                )}
                <HomeRowContent>
                  <H4>
                    {idea.name}
                    <Subheader>
                      <p>
                        in{" "}
                        <HomeLink to={`/challenges/${idea.challenge}`}>
                          {idea.ownerChallenge?.name}
                        </HomeLink>
                        ,{" "}
                        {idea.submittedAt || idea.updatedAt ? (
                          <>
                            {isDraft ? "updated " : "submitted "}
                            {formatDateRelative(
                              idea.submittedAt || idea.updatedAt,
                              { addSuffix: true },
                            )}
                          </>
                        ) : null}
                      </p>

                      <p>
                        {isDraft && deadlineStatus ? (
                          <Label
                            color={deadlineStatus.color}
                            basic={deadlineStatus.basic}
                            size="small"
                          >
                            <Icon icon={CalendarIcon} />
                            Deadline: {deadlineStatus.text}
                          </Label>
                        ) : null}
                      </p>
                    </Subheader>
                  </H4>
                </HomeRowContent>
                <HomeRowActions>
                  <ButtonLink
                    color="primary"
                    variant="outline"
                    size="small"
                    to={`/ideas/${idea._id}`}
                  >
                    {t(
                      isDraft && !deadlineStatus?.elapsed
                        ? "home.ideas.continue"
                        : "home.ideas.view",
                    )}
                  </ButtonLink>
                </HomeRowActions>
              </HomeRow>
            );
          })}
        </HomeRowContainer>
      </CardContent>
      {ideas.length > countPerColumn * 2 ? (
        <CardContent color="primary" textAlign="right">
          <Button
            color="primary"
            variant="ghost"
            size="small"
            onClick={() => setShowMore(!showMore)}
          >
            <Icon icon={showMore ? ArrowUpIcon : ArrowDownIcon} />
            {showMore ? t("home.ideas.showLess") : t("home.ideas.showMore")}
          </Button>
        </CardContent>
      ) : null}
    </HomeBlock>
  );
};

export default UserIdeasBlock;
